
import { Component, Mixins } from 'vue-property-decorator'
import NavigationIcon from '@/components/promoter_signup/NavigationIcon.vue'
import OrganizationForm from '@/components/promoter_signup/OrganizationForm.vue'
import ContactPersonForm from '@/components/promoter_signup/ContactPersonForm.vue'
import Summary from '@/components/promoter_signup/Summary.vue'
import ToastMixin from '@/mixins/ToastMixin'
import axios from 'axios'
import { API_URLS } from '@/utils/helpers'
import { IPromoterSignupForm, IContactPersonSignUpForm } from '@/types/promoterSignup'
import CompletitionLayout from "@/components/processing_steps/commons/CompletitionLayout.vue"
import OrganisationSummaryMixin from "@/components/processing_steps/organization/mixins/OrganizationSummaryMixin.vue"
import TermsForm from "@/components/processing_steps/commons/forms/TermsForm.vue"

@Component({
  components: {
    TermsForm,
    CompletitionLayout,
    NavigationIcon,
    OrganizationForm,
    Summary,
    ContactPersonForm
  }
})
export default class PromoterSignup extends Mixins(ToastMixin, OrganisationSummaryMixin) {
  currentStep: 'organization' | 'contactPerson' | 'summary' = 'organization'
  userWants: 'organization' | 'contactPerson' | 'summary' = 'organization'
  passwordState: 'weak' | 'medium' | 'strong' | 'notMatching' | null = null
  passwordMaxLength = 40
  organizationCompleted = false
  contactPersonCompleted = false
  confirmAdressModalOpen = false
  organizationAddressConfirmedByUser = false
  termsAccepted = false
  formattedAddress = {
    street: '',
    postal_code: '',
    city: '',
    country: '',
    longitude: 0,
    latitude: 0
  }

  organizationForm: IPromoterSignupForm = {
    title: '',
    email: '',
    street: '',
    additional_information: '',
    postal_code: '',
    city: '',
    country: 'DE',
    region: '',
    phone: '',
    fax: '',
    website: '',
    longitude: 0,
    latitude: 0
  }

  organizationFormErrors: IPromoterSignupForm = {
    title: '',
    email: '',
    street: '',
    additional_information: '',
    postal_code: '',
    city: '',
    country: '',
    region: '',
    phone: '',
    fax: '',
    website: ''
  }

  organizationRequiredFields = ['title', 'email', 'street', 'postal_code', 'city', 'country', 'region', 'phone']

  contactPersonForm: IContactPersonSignUpForm = {
    term_of_address: 1,
    first_name: '',
    last_name: '',
    email_contact: '',
    username: '',
    password: '',
    password_repeat: '',
    phone_contact: ''
  }

  contactPersonFormErrors: IContactPersonSignUpForm = {
    term_of_address: '',
    first_name: '',
    last_name: '',
    email_contact: '',
    username: '',
    password: '',
    password_repeat: '',
    phone_contact: ''
  }

  contactPersonRequiredFields = ['term_of_address', 'first_name', 'last_name', 'email_contact', 'username', 'password', 'password_repeat', 'phone_contact']

  get emptyFieldError (): string {
    return this.$gettext('This field is required')
  }

  get emptyFieldsErroryMsg (): string {
    return this.$gettext('Please fill in all required fields')
  }

  get invalidInputErrorMsg (): string {
    return this.$gettext('Please check your input')
  }

  get summaryItems () {
    return [
      this.organizationDetails,
      this.contactPersonDetails
    ]
  }

  get organizationDetails (): { title: string, items: { title: string, values: string[]}[]} {
    return {
      title: this.$gettext('Organization'),
      items: [
        {
          title: this.$gettext('Organizations name'),
          values: [this.organizationForm.title]
        },
        {
          title: this.$gettext('Email address'),
          values: [this.organizationForm.email]
        },
        {
          title: this.$gettext('Address'),
          values: [this.organizationForm.street, this.organizationForm.postal_code, this.organizationForm.city, this.organizationForm.country]
        },
        {
          title: this.$gettext('Phone'),
          values: [this.organizationForm.phone]
        }
      ]
    }
  }

  get contactPersonDetails () {
    return {
      title: this.$gettext('User'),
      items: [
        {
          title: this.$gettext('Name'),
          values: [this.contactPersonAdressableName]
        },
        {
          title: this.$gettext('Email address'),
          values: [this.contactPersonForm.email_contact]
        },
        {
          title: this.$gettext('Phone'),
          values: [this.contactPersonForm.phone_contact]
        }
      ]
    }
  }

  get contactPersonAdressableName (): string {
    const termOfAdress = this.contactPersonForm.term_of_address === 1 ? this.$gettext("Term of address") : this.contactPersonForm.term_of_address === 2 ? this.$gettext("Mrs.") : this.$gettext("Mr.")
    return termOfAdress + " " + this.contactPersonForm.first_name + " " + this.contactPersonForm.last_name
  }

  async gotoStep (key: 'organization' | 'contactPerson' | 'summary'): Promise<void> {
    this.userWants = key
    if (key === 'organization') {
      this.setCurrentStep('organization')
    } else if (key === 'contactPerson') {
      await this.validateOrganization()
      if (this.organizationCompleted) {
        this.setCurrentStep('contactPerson')
      } else {
        this.setCurrentStep('organization')
      }
    } else if (key === 'summary') {
      await this.validateOrganization()
      if (!this.organizationCompleted && !this.organizationAddressConfirmedByUser) {
        this.setCurrentStep('organization')
        return
      }
      await this.validateContactPerson()
      if (this.contactPersonCompleted) {
        this.setCurrentStep('summary')
      } else {
        this.setCurrentStep('contactPerson')
      }
    }
  }

  async validateOrganizationAddress (): Promise<boolean> {
    if (this.organizationForm.street && this.organizationForm.postal_code && this.organizationForm.city && this.organizationForm.country) {
      const geoLocResponse = await this.fetchLocationData()
      if (geoLocResponse && !this.organizationAddressConfirmedByUser) {
        if (geoLocResponse.data && geoLocResponse.data && geoLocResponse.data.results.length > 0 && geoLocResponse.data.results[0].partial_match) {
          this.confirmAdressModalOpen = true
          const address = geoLocResponse.data.results[0].formatted_address
          const cityAndPostalCode = address.split(',')[1]
          const street = address.split(',')[0] || this.organizationForm.street
          const postalCode = cityAndPostalCode ? cityAndPostalCode.split(' ')[1] : this.organizationForm.postal_code
          const city = cityAndPostalCode ? cityAndPostalCode.split(' ')[2] : this.organizationForm.city
          const country = address.split(',')[2] || this.organizationForm.country
          const longitude = geoLocResponse.data.results[0].geometry.location.lng
          const latitude = geoLocResponse.data.results[0].geometry.location.lat
          this.formattedAddress = {
            street,
            postal_code: postalCode,
            city,
            country,
            longitude,
            latitude
          }
          this.organizationForm.latitude = latitude
          this.organizationForm.longitude = longitude
          return false
        } else if (geoLocResponse.status === 200) {
          if (geoLocResponse.data && geoLocResponse.data && geoLocResponse.data.results.length > 0) {
            const geoLoc = geoLocResponse.data.results[0]?.geometry?.location ?? { lat: this.organizationForm.latitude, lng: this.organizationForm.longitude }
            this.organizationForm.latitude = geoLoc.lat
            this.organizationForm.longitude = geoLoc.lng
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      }
    } else {
      return false
    }
  }

  checkEmptyFields (form: IPromoterSignupForm | IContactPersonSignUpForm, requiredFields: string[], formErrors: IPromoterSignupForm | IContactPersonSignUpForm): boolean {
    let valid = true
    for (const key in form) {
      if (form[key] === '' || form[key] === undefined) {
        if (requiredFields.includes(key)) {
          if (["email", "email_contact"].includes(key)) {
            if (!this.isValidEmail(form[key])) {
              formErrors[key] = this.$gettext('Please enter a valid email address')
              valid = false
            }
          } else {
            formErrors[key] = this.emptyFieldError
            valid = false
          }
        }
      } else {
        formErrors[key] = ''
      }
    }
    return valid
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldErrorsFromResponse (error: any, formErrors: IPromoterSignupForm | IContactPersonSignUpForm, stepKey: 'organization' | 'contactPerson'): void {
    for (const key in error.response.data) {
      formErrors[key] = error.response.data[key]
      if (stepKey === 'organization') {
        this.organizationCompleted = false
        this.setCurrentStep('organization')
      } else if (stepKey === 'contactPerson') {
        this.setCurrentStep('contactPerson')
        this.contactPersonCompleted = false
      }
    }
  }

  async validateOrganization (): Promise<void> {
    const noEmptyFields = this.checkEmptyFields(this.organizationForm, this.organizationRequiredFields, this.organizationFormErrors)
    if (!noEmptyFields) {
      this.organizationCompleted = false
      this.setCurrentStep('organization')
      this.makeToast('danger', 'Error', this.emptyFieldsErroryMsg)
      return
    }
    const data = {
      title: this.organizationForm.title,
      email: this.organizationForm.email,
      street: this.organizationForm.street,
      additional_information: this.organizationForm.additional_information,
      postal_code: this.organizationForm.postal_code.toString(),
      city: this.organizationForm.city,
      country: this.organizationForm.country,
      phone: this.organizationForm.phone,
      website: this.organizationForm.website,
      longitude: 0,
      latitude: 0,
      promoter_email: this.organizationForm.email
    }
    await axios.post(API_URLS.SIGNUP.VALIDATE, data).then(async () => {
      this.organizationCompleted = true
      const validAddress = await this.validateOrganizationAddress()
      if (!validAddress && !this.organizationAddressConfirmedByUser) {
        this.organizationCompleted = false
      } else {
        this.organizationCompleted = true
      }
    }).catch((error) => {
      this.setFieldErrorsFromResponse(error, this.organizationFormErrors, 'organization')
      this.setCurrentStep('organization')
      this.makeToast('danger', 'Error', this.invalidInputErrorMsg)
    })
  }

  async validateContactPerson (): Promise<void> {
    let valid = true
    for (const key in this.contactPersonForm) {
      if (this.contactPersonForm[key] === '' || this.contactPersonForm[key] === undefined) {
        if (this.contactPersonRequiredFields.includes(key)) {
          this.contactPersonFormErrors[key] = this.emptyFieldError
          valid = false
          this.setCurrentStep('contactPerson')
        }
      } else {
        this.contactPersonFormErrors[key] = ''
      }
    }
    if (!this.validatePassword()) {
      valid = false
    }
    if (!valid) {
      this.contactPersonCompleted = false
      this.setCurrentStep('contactPerson')
      this.makeToast('danger', 'Error', this.emptyFieldsErroryMsg)
      return
    }
    const data = this.contactPersonForm
    await axios.post(API_URLS.SIGNUP.VALIDATE, data).then(() => {
      this.contactPersonCompleted = true
    }).catch((error) => {
      for (const key in error.response.data) {
        this.contactPersonFormErrors[key] = error.response.data[key]
        this.contactPersonCompleted = false
      }
      this.makeToast('danger', 'Error', this.invalidInputErrorMsg)
    })
  }

  setCurrentStep (key: 'organization' | 'contactPerson' | 'summary'): void {
    if (key === 'organization') {
      this.organizationAddressConfirmedByUser = false
    }
    this.currentStep = key
  }

  isCurrentStep (key: 'organization' | 'contactPerson' | 'summary'): boolean {
    return this.currentStep === key
  }

  isValidEmail (value: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    return emailRegex.test(value)
  }

  validateEmail(key: 'organization' | 'contactPerson'): void {
    let msg = this.$gettext('Please check your input')
    if (key === 'organization' && !this.isValidEmail(this.organizationForm.email)) {
      this.organizationFormErrors.email = this.$gettext('Please enter a valid email address')
      return
    }
    if (key === 'contactPerson' && !this.isValidEmail(this.contactPersonForm.email_contact)) {
      this.contactPersonFormErrors.email_contact = this.$gettext('Please enter a valid email address')
      return
    }
    let data = {}
    if (key === 'organization') {
      data = {
        email: this.organizationForm.email,
        promoter_email: this.organizationForm.email
      }
    } else if (key === 'contactPerson') {
      data = { email_contact: this.contactPersonForm.email_contact }
    }
    axios.post(API_URLS.SIGNUP.VALIDATE, data).then(() => {
      if (key === 'organization') {
        this.organizationFormErrors.email = ''
      } else if (key === 'contactPerson') {
        this.contactPersonFormErrors.email_contact = ''
      }
    }).catch((error) => {
      if (key === 'organization') {
        this.organizationFormErrors.email = error.response.data.email
        msg = this.organizationFormErrors.email
      } else if (key === 'contactPerson') {
        this.contactPersonFormErrors.email_contact = error.response.data.email_contact
        msg = this.contactPersonFormErrors.email_contact
      }
      this.makeToast('danger', this.$gettext('Error'), msg)
    })
  }

  validateOrgaTitle (): void {
    const data = { title: this.organizationForm.title }
    axios.post(API_URLS.SIGNUP.VALIDATE, data).then(() => {
      this.organizationFormErrors.title = ''
    }).catch((error) => {
      this.organizationFormErrors.title = error.response.data.title
      this.makeToast('danger', 'Error', 'Please check your input')
    })
  }

  validateUsername (): void {
    const data = { username: this.contactPersonForm.username }
    axios.post(API_URLS.SIGNUP.VALIDATE, data).then(() => {
      this.contactPersonFormErrors.username = ''
    }).catch((error) => {
      this.contactPersonFormErrors.username = error.response.data.username
      this.makeToast('danger', 'Error', 'Please check your input')
    })
  }

  validatePassword (): boolean {
    if (this.contactPersonForm.password !== this.contactPersonForm.password_repeat) {
      this.passwordState = 'notMatching'
      this.contactPersonFormErrors.password = 'Passwords do not match'
      this.contactPersonFormErrors.password_repeat = 'Passwords do not match'
      return false
    }
    this.contactPersonFormErrors.password = ''
    this.contactPersonFormErrors.password_repeat = ''
    /* eslint-disable no-useless-escape */
    const regexStrong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!@#\$%\^&\*)(}{_.+|~=`'";\/?,:<>])(?=.{8,})/
    const isStrongPassword = regexStrong.test(this.contactPersonForm.password)
    if (this.contactPersonForm.password.length > this.passwordMaxLength) {
      this.passwordState = null
      const maxLengtMsg = this.$gettext(`Password is too long. Maximum length is 40 characters.`)
      this.contactPersonFormErrors.password = maxLengtMsg
      this.contactPersonFormErrors.password_repeat = maxLengtMsg
      return false
    }
    if (isStrongPassword) {
      this.passwordState = 'strong'
      return true
    } else {
      this.passwordState = 'weak'
      this.contactPersonFormErrors.password = this.passwordRegexExplain
      this.contactPersonFormErrors.password_repeat = this.passwordRegexExplain
      return false
    }
  }

  get passwordRegexExplain (): string {
    return this.$gettext('Password must contain at least 8 characters, including uppercase, lowercase, numbers and special characters.')
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async fetchLocationData (): Promise<any> {
    const locationUrl = "/api/v3/actions/codeaddress/"
    const address = { address: `${this.organizationForm.street} ${this.organizationForm.postal_code} ${this.organizationForm.city}, ${this.organizationForm.country}` }
    const res = await axios.post(locationUrl, address).then((response) => {
      return response
    }).catch(() => {
      return null
    })
    return res
  }

  async sendData (): Promise<void> {
    if (!this.termsAccepted) {
      this.termsToast()
    } else {
      const data = {
        ...this.organizationForm,
        ...this.contactPersonForm,
        terms: true // todo make it a prop
      }
      axios.post('/api/v3/actions/organization-signup/', data).then((response) => {
        window.location.href = response.data.success_url
      }).catch((error) => {
        this.setFieldErrorsFromResponse(error, this.contactPersonFormErrors, 'contactPerson')
        this.setFieldErrorsFromResponse(error, this.organizationFormErrors, 'organization')
      })
    }
  }

  changeAddress (): void {
    this.confirmAdressModalOpen = false
    this.organizationForm.street = this.formattedAddress.street || this.organizationForm.street
    this.organizationForm.postal_code = this.formattedAddress.postal_code || this.organizationForm.postal_code
    this.organizationForm.city = this.formattedAddress.city || this.organizationForm.city
    this.organizationForm.longitude = this.formattedAddress.longitude ? this.formattedAddress.longitude : this.organizationForm.longitude ? this.organizationForm.longitude : 0
    this.organizationForm.latitude = this.formattedAddress.latitude ? this.formattedAddress.latitude : this.organizationForm.latitude ? this.organizationForm.latitude : 0
    this.organizationAddressConfirmedByUser = true
    this.gotoStep(this.userWants)
  }

  keepAddress (): void {
    this.organizationAddressConfirmedByUser = true
    this.confirmAdressModalOpen = false
    this.gotoStep(this.userWants)
  }

  termsToast(): void {
    this.makeToast('danger', this.$gettext('Error'), this.$gettext('Please accept the terms'))
  }

  privacyToast(): void {
    this.makeToast('danger', this.$gettext('Error'), this.$gettext('Please accept the terms of use and privacy policy'))
  }

  onRegionOptionsFetched (regionsAvailable: boolean): void {
    if (!regionsAvailable) {
      const index = this.organizationRequiredFields.indexOf('region')
      if (index > -1) {
        this.organizationRequiredFields.splice(index, 1)
      }
      this.organizationFormErrors.region = ''
    } else {
      if (!this.organizationRequiredFields.includes('region')) {
        this.organizationRequiredFields.push('region')
      }
    }
  }
}
